import React, { CSSProperties, FC, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import s from './MediaImg.module.sass'
import { GetResizedLinkWithCustomSize, IsAfishaImage, IsAlreadyResized } from '@shared/ui/Media/MediaImg/helpers/helper'
import { ImageModel } from '@shared/ui/Media/MediaImg/types/types'
import { m } from '@shared/ui/Media/MediaImg/helpers/m'
import { useIsMounted } from '@shared/lib/hooks/useIsMounted'
import { getPlaceholderImage } from '@shared/lib/helpers/getPlaceholderImage'

type ReactElementOrRenderFunction = (React.ReactElement | null) | ((props: Props) => React.ReactElement | null)

interface CommonProps
  extends Omit<
    PropsOf.Img,
    'width' | 'height' | 'src' | 'srcSet' | 'loading' | 'decoding' | 'placeholder' | 'children'
  > {
  image: ImageModel | null
  placeholder?: ReactElementOrRenderFunction
  children?: ReactElementOrRenderFunction

  objectId?: number
  flexible?: boolean
  circle?: boolean
  avatar?: boolean
  shrink?: boolean
  hover?: boolean | '2' | '3'
  fallback?: boolean
  dimmed?: 'gradient' | 'box-shadow' | false
  borderRadius?: CSSProperties['borderRadius']
  onLoad?: () => void
}

// Необходимо указать либо высоту либо ширину или оба параметра
export type TypeWidthHeight =
  | { height: number; width: number }
  | { height: number; width?: never }
  | { height?: never; width: number }

type Props = CommonProps & TypeWidthHeight

const MediaImg: FC<Props> = (props) => {
  const {
    className,
    title,
    width,
    height,
    image,
    placeholder = ({ className, width, height }) => <div>test</div>,
    // placeholder = ({ className, width, height }) => <Skeleton className={className} width={width} height={height} />,
    children: fallback = Fallback,
    avatar = false,
    objectId = 1,
    flexible = false,
    circle = false,
    shrink = false,
    hover = false,
    dimmed = 'box-shadow',
    borderRadius = circle ? '50%' : undefined,
    style,
    onLoad,
    ...rest
  } = props

  const { onStillMounted } = useIsMounted()

  const [isForcedFallback, setIsForcedFallback] = useState(false)

  const styles_ = useMemo(
    () => ({
      ...(image?.Url && { backgroundImage: `url(${getPreviewSrc(image)})` }),
    }),
    [image?.Url],
  )

  const isLazy = placeholder !== null

  const ref = useRef<HTMLImageElement | null>(null)

  useEffect(() => {
    const img = ref.current

    if (img) {
      if (img.dataset.loaded === 'true') {
        // Изображение уже загружено
      } else if (img.dataset.error === 'true') {
        // Ошибка уже произошла
      } else {
        const handleLoad = () => {
          img.dataset.loaded = 'true'
          onLoad && onLoad()

          const isInvalid = img.naturalWidth === 0 || img.naturalHeight === 0
          const isProbablyPlaceholder = img.currentSrc.includes('data:image')

          if (isInvalid || isProbablyPlaceholder) {
            img.dataset.error = 'true'

            onStillMounted(() => setIsForcedFallback(true))
          }
        }

        const handleError = () => {
          img.dataset.error = 'true'
          onStillMounted(() => setIsForcedFallback(true))
        }

        if (img.complete) {
          setTimeout(handleLoad, 0)
        } else {
          img.dataset.loaded = 'false'
          img.addEventListener('load', handleLoad, { once: true })
          img.addEventListener('error', handleError, { once: true })
        }
      }
    }
  }, [])

  const renderFallback = useCallback(
    () => (typeof fallback === 'function' ? fallback(props) : fallback),
    [fallback, props],
  )

  if (isForcedFallback) {
    return renderFallback()
  }

  if (image?.Url) {
    const { src, srcSet } = getSrc(image, { width, height })

    return (
      <picture
        className={mapPropsToPictureClassName({ className, flexible, shrink, hover, dimmed })}
        title={title}
        data-img
        style={{
          ...(borderRadius && {
            ['--border-radius' as string]: typeof borderRadius === 'number' ? `${borderRadius}px` : borderRadius,
          }),
          ...style,
        }}>
        <img
          {...rest}
          width={width}
          height={height}
          src={image.Url}
          srcSet={`${src} 1x, ${srcSet} 2x`}
          loading={isLazy ? 'lazy' : 'eager'}
          // decoding='async'
          style={styles_}
          ref={ref}
        />
      </picture>
    )
  }

  return renderFallback()
}

const mapPropsToFallbackClassName = ({
  className,
  flexible,
  avatar,
  fallback = true,
  shrink,
  dimmed,
  hover,
}: Pick<Props, 'className' | 'flexible' | 'fallback' | 'shrink' | 'dimmed' | 'hover' | 'avatar'>) =>
  m(
    s,
    {
      fallback,
      flexible,
      shrink,
      avatar,
      ...(hover && { [`hover_${(hover === true ? '3' : hover) satisfies Props['hover']}`]: true }),
      ...(dimmed && { [dimmed]: true }),
    },
    className,
  )

const mapPropsToPictureClassName = ({
  className,
  flexible,
  shrink,
  dimmed,
  hover,
}: Pick<Props, 'className' | 'flexible' | 'shrink' | 'dimmed' | 'hover'>) =>
  m(
    s,
    {
      picture: true,
      flexible,
      shrink,
      ...(hover && { [`hover_${(hover === true ? '3' : hover) satisfies Props['hover']}`]: true }),
      ...(dimmed && { [dimmed]: true }),
    },
    className,
  )

const Fallback = (props: Props, src: string = getPlaceholderImage(props.objectId)) => {
  const { className, flexible = false, shrink = false, hover = false, dimmed = 'box-shadow' } = props

  return (
    <div
      className={mapPropsToFallbackClassName({ className, flexible, shrink, dimmed, hover })}
      style={{
        ['--fallback-src' as string]: `url(${src})`,
        width: props.flexible ? '100%' : props.width,
        height: props.flexible ? '100%' : props.height,
        aspectRatio: `${props.width} / ${props.height}`,
        borderRadius: props.circle ? '50%' : props.borderRadius,
      }}
    />
  )
}

const getPreviewSrc = (image: ImageModel) => {
  let size = null

  if (typeof image.Width === 'number' && typeof image.Height === 'number') {
    size =
      image.Width > image.Height
        ? { height: 8, width: 8 * (image.Width / image.Height || 1) }
        : { width: 8, height: 8 * (image.Height / image.Width || 1) }
  } else {
    size = { width: image.Width ? 8 : undefined, height: image.Height ? 8 : undefined }
  }

  return getSrc(image, size).src
}

const getSrc = (image: ImageModel, size: Pick<Props, 'width' | 'height'>) => {
  if (!IsAfishaImage(image.Url) || IsAlreadyResized(image.Url)) {
    return {
      src: image.Url,
      srcSet: image.Url,
    }
  }

  const { width, height } = size

  return {
    src: GetResizedLinkWithCustomSize(image, { width, height }),
    srcSet: GetResizedLinkWithCustomSize(image, { width, height }, 2),
  }
}

export default memo(MediaImg)
